import { cn } from '@/lib/utils';
import { CalendarIcon, Clock } from 'lucide-react';
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn-ui/popover';
import { Button } from './shadcn-ui/button';
import { Calendar } from './shadcn-ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';

const toDate = value => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

export function DateTimePicker({
  value,
  onChange,
  bgColor = 'bg-transparent',
  icon: Icon = CalendarIcon,
  placeholder = 'Select date and time',
  dateFormat = 'PPP p',
  className,
  minDate,
  maxDate,
  error,
  disabled = false,
}) {
  const parsedValue = React.useMemo(() => toDate(value), [value]);

  const [open, setOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(parsedValue);

  // Convert 24-hour to 12-hour format for display
  const get12HourFormat = date => {
    if (!date) return { hours: '12', minutes: '00', ampm: 'AM' };
    const hours24 = date.getHours();
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    return {
      hours: hours12.toString().padStart(2, '0'),
      minutes: date.getMinutes().toString().padStart(2, '0'),
      ampm: hours24 >= 12 ? 'PM' : 'AM',
    };
  };

  const initialTime = get12HourFormat(parsedValue);
  const [hours, setHours] = React.useState(initialTime.hours);
  const [minutes, setMinutes] = React.useState(initialTime.minutes);
  const [ampm, setAmpm] = React.useState(initialTime.ampm);

  // Convert 12-hour to 24-hour format
  const convertTo24Hour = (hrs, mins, ampm) => {
    let hours24 = parseInt(hrs);
    if (ampm === 'PM' && hours24 !== 12) {
      hours24 += 12;
    } else if (ampm === 'AM' && hours24 === 12) {
      hours24 = 0;
    }
    return hours24;
  };

  const handleDateSelect = date => {
    if (!date) {
      setSelectedDate(null);
      return;
    }

    const hours24 = convertTo24Hour(hours, minutes, ampm);
    const newDate = new Date(date);
    newDate.setHours(hours24, parseInt(minutes));
    setSelectedDate(newDate);
  };

  const handleApply = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (selectedDate) {
      // Validate that time fields are not empty
      if (hours === '' || minutes === '' || isNaN(parseInt(hours)) || isNaN(parseInt(minutes))) {
        // Optionally show an error or just return without applying
        return;
      }

      const hours24 = convertTo24Hour(hours, minutes, ampm);
      const newDate = new Date(selectedDate);
      newDate.setHours(hours24, parseInt(minutes));
      onChange(newDate);
    }
    setOpen(false);
  };

  const handleHoursChange = e => {
    const val = e.target.value;
    // Allow empty string, single digit, or valid 1-12 range
    if (val === '' || val === '0' || (parseInt(val) >= 1 && parseInt(val) <= 12)) {
      setHours(val);
      if (selectedDate) {
        // Use val directly, and only fallback to '12' if empty
        const hoursValue = val === '' ? '12' : val;
        const hours24 = convertTo24Hour(hoursValue, minutes, ampm);
        const newDate = new Date(selectedDate);
        newDate.setHours(hours24, parseInt(minutes) || 0);
        setSelectedDate(newDate);
      }
    }
  };

  const handleMinutesChange = e => {
    const val = e.target.value;
    setMinutes(val);
    if (selectedDate) {
      const hours24 = convertTo24Hour(hours, val || minutes, ampm);
      const newDate = new Date(selectedDate);
      newDate.setMinutes(parseInt(val || minutes));
      setSelectedDate(newDate);
    }
  };

  const handleAmpmChange = newAmpm => {
    setAmpm(newAmpm);
    if (selectedDate) {
      const hours24 = convertTo24Hour(hours, minutes, newAmpm);
      const newDate = new Date(selectedDate);
      newDate.setHours(hours24);
      setSelectedDate(newDate);
    }
  };

  // Update local state when value prop changes
  // React.useEffect(() => {
  //   if (parsedValue) {
  //     const time12 = get12HourFormat(parsedValue);
  //     setHours(time12.hours);
  //     setMinutes(time12.minutes);
  //     setAmpm(time12.ampm);
  //     setSelectedDate(parsedValue);
  //   }
  // }, [parsedValue]);
  React.useEffect(() => {
    if (parsedValue) {
      // Only update if the date value actually changed (not just time)
      const currentDateOnly = selectedDate
        ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())
        : null;
      const newDateOnly = new Date(parsedValue.getFullYear(), parsedValue.getMonth(), parsedValue.getDate());

      // Only reset if the date part changed, or if selectedDate is null
      if (!selectedDate || currentDateOnly?.getTime() !== newDateOnly.getTime()) {
        const time12 = get12HourFormat(parsedValue);
        setHours(time12.hours);
        setMinutes(time12.minutes);
        setAmpm(time12.ampm);
        setSelectedDate(parsedValue);
      } else {
        // If only time changed externally, update time fields but keep the date
        const time12 = get12HourFormat(parsedValue);
        setHours(time12.hours);
        setMinutes(time12.minutes);
        setAmpm(time12.ampm);
        // Update selectedDate with new time but keep the date part
        const updatedDate = new Date(selectedDate);
        updatedDate.setHours(parsedValue.getHours(), parsedValue.getMinutes());
        setSelectedDate(updatedDate);
      }
    } else if (!parsedValue && selectedDate) {
      // If value becomes null, reset everything
      setHours('12');
      setMinutes('00');
      setAmpm('AM');
      setSelectedDate(null);
    }
  }, [parsedValue]);

  return (
    <div className={cn('flex flex-col space-y-1', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            disabled={disabled}
            className={cn(
              'w-full justify-between rounded-xl text-left font-normal',
              !value && 'text-muted-foreground',
              bgColor,
              error && 'border-red-500',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {value ? format(value, dateFormat) : placeholder}
            <div className="flex items-center gap-1">
              <Icon className="h-4 w-4 opacity-50" />
              <Clock className="mt-0.5 h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto bg-white p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
            disabled={date => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              return false;
            }}
          />

          <div className="space-y-3 border-t p-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Time</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={hours}
                  onChange={handleHoursChange}
                  placeholder="HH"
                  disabled={disabled}
                  className="flex h-9 w-16 rounded-md border border-input bg-transparent px-3 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-lg font-medium">:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={handleMinutesChange}
                  placeholder="MM"
                  disabled={disabled}
                  className="flex h-9 w-16 rounded-md border border-input bg-transparent px-3 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                <select
                  value={ampm}
                  onChange={e => handleAmpmChange(e.target.value)}
                  disabled={disabled}
                  className="flex h-9 w-20 rounded-md border border-input bg-transparent px-2 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
            <Button onClick={handleApply} className="w-full" size="sm" disabled={disabled}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
